<template>
  <div class="home">
    <Header />
    <main>
      <!-- Banner 轮播 -->
      <section class="banner-section">
        <div class="swiper-container" ref="swiperContainer">
          <div class="swiper-wrapper">
            <div
              v-for="(banner, index) in banners"
              :key="index"
              class="swiper-slide"
              :style="{ backgroundImage: `url(${banner.imageUrl || defaultBanner})` }"
            >
              <div class="banner-content">
                <h1 class="banner-title">{{ banner.title || '雁南时空科技' }}</h1>
                <div class="banner-subtitle">
                  <span
                    v-if="formatSubtitle(banner.subtitle).tagline"
                    class="subtitle-tag"
                  >
                    {{ formatSubtitle(banner.subtitle).tagline }}
                  </span>
                  <p class="subtitle-text">
                    {{ formatSubtitle(banner.subtitle).text }}
                  </p>
                </div>
                <a 
                  v-if="isExternalUrl(banner.linkUrl)" 
                  :href="banner.linkUrl" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="btn-primary"
                >了解更多</a>
                <router-link 
                  v-else 
                  :to="banner.linkUrl || '/business'" 
                  class="btn-primary"
                >了解更多</router-link>
              </div>
              <div class="banner-overlay"></div>
            </div>
          </div>
          <div class="swiper-pagination"></div>
          <div class="swiper-button-prev"></div>
          <div class="swiper-button-next"></div>
        </div>
      </section>

      <!-- 关于我们 -->
      <section class="about-section">
        <div class="container">
          <div class="section-title">
            <h2>关于我们</h2>
            <p>公司作为大疆行业级无人机官方授权合作伙伴，专注于为政企客户提供涵盖无人机系统集成、定制化软件开发与低空运营服务的全方位解决方案</p>
          </div>
          <div class="about-grid">
            <div
              v-for="(item, index) in aboutItems"
              :key="index"
              class="about-card card"
            >
              <div class="card-icon">
                <i :class="item.icon"></i>
              </div>
              <h3>{{ item.title }}</h3>
              <p>{{ item.description }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- 业务板块 -->
      <section class="business-section">
        <div class="container">
          <div class="section-title">
            <h2>业务板块</h2>
            <p>以无人机系统为核心，构建从智能硬件、软件平台到数据运营的完整服务闭环，致力于为各行业提供前沿的低空数字化解决方案</p>
          </div>
          <div class="business-grid">
            <div
              v-for="(business, index) in businesses"
              :key="index"
              class="business-card card"
            >
              <div class="business-image">
                <img :src="business.imageUrl || defaultBusinessImage" :alt="business.title">
              </div>
              <div class="business-content">
                <h3>{{ business.title }}</h3>
                <p>{{ business.description }}</p>
                <router-link :to="`/business/${business.id}`" class="btn-outline">查看详情</router-link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 成功案例 -->
      <section class="cases-section">
        <div class="container">
          <div class="section-title">
            <h2>成功案例</h2>
            <p>展示我们的专业能力和服务成果</p>
          </div>
          <div class="case-filter">
            <button
              v-for="category in caseCategories"
              :key="category.value"
              :class="['filter-btn', { active: activeCategory === category.value }]"
              @click="filterCases(category.value)"
            >
              {{ category.label }}
            </button>
          </div>
          <div class="cases-grid">
            <div
              v-for="(caseItem, index) in filteredCases"
              :key="index"
              class="case-card card"
              @click="viewCase(caseItem)"
            >
              <div class="case-image">
                <img :src="getCaseImageUrl(caseItem.imageUrl)" :alt="caseItem.title" @error="handleCaseImageError">
                <div class="case-overlay">
                  <h4>{{ caseItem.title }}</h4>
                  <p>{{ caseItem.category }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 数据统计 -->
      <section class="stats-section">
        <div class="container">
          <div class="stats-grid">
            <div
              v-for="(stat, index) in stats"
              :key="index"
              class="stat-item"
            >
              <div class="stat-icon">
                <i :class="stat.icon"></i>
              </div>
              <div class="stat-number" :data-target="stat.value">{{ stat.value }}</div>
              <div class="stat-label">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- 新闻动态 -->
      <section class="news-section">
        <div class="container">
          <div class="section-title">
            <h2>新闻动态</h2>
            <p>了解最新的行业资讯和公司动态</p>
          </div>
          <div class="news-grid">
            <div
              v-for="(news, index) in latestNews"
              :key="index"
              class="news-card card"
            >
              <div class="news-image">
                <img :src="news.imageUrl || defaultNewsImage" :alt="news.title">
                <div class="news-date">
                  <span>{{ formatDate(news.publishTime) }}</span>
                </div>
              </div>
              <div class="news-content">
                <h3>{{ news.title }}</h3>
                <p>{{ news.summary }}</p>
                <router-link :to="`/news/${news.id}`" class="read-more">阅读更多 →</router-link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    <Footer />
    <BackToTop />
  </div>
</template>

<script>
import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import BackToTop from '@/components/BackToTop.vue'
import { bannerApi, businessApi, caseApi, newsApi, statsApi } from '@/api'
import Swiper, { Navigation, Pagination, Autoplay, EffectFade } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-fade'

export default {
  name: 'Home',
  components: {
    Header,
    Footer,
    BackToTop
  },
  data() {
    return {
      banners: [],
      businesses: [],
      cases: [],
      latestNews: [],
      stats: [],
      activeCategory: 'all',
      swiper: null,
      caseCategories: [
        { label: '全部', value: 'all' },
        { label: '无人机销售', value: 'sales' },
        { label: '软件平台开发', value: 'software' },
        { label: '低空数据运营服务', value: 'service' },
        { label: '高精度实景三维建模', value: 'modeling' }
      ],
      aboutItems: [
        {
          icon: 'el-icon-trophy',
          title: '专业团队',
          description: '拥有丰富的无人机行业经验和专业技术团队'
        },
        {
          icon: 'el-icon-star-on',
          title: '优质产品',
          description: '代理大疆全系列无人机产品，品质保证'
        },
        {
          icon: 'el-icon-service',
          title: '完善服务',
          description: '提供从销售到售后的全方位服务支持'
        },
        {
          icon: 'el-icon-connection',
          title: '技术创新',
          description: '自主研发数字高速、智能体等创新平台'
        }
      ],
      defaultBanner: 'https://via.placeholder.com/1920x800/1890ff/ffffff?text=雁南时空科技',
      defaultBusinessImage: 'https://via.placeholder.com/400x300/52c41a/ffffff?text=业务板块',
      defaultCaseImage: 'https://via.placeholder.com/400x300/ff4d4f/ffffff?text=成功案例',
      defaultNewsImage: 'https://via.placeholder.com/400x250/1890ff/ffffff?text=新闻动态'
    }
  },
  mounted() {
    this.loadData()
  },
  computed: {
    filteredCases() {
      if (this.activeCategory === 'all') {
        return this.cases
      }
      return this.cases.filter(item => item.category === this.activeCategory)
    }
  },
  beforeDestroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true)
    }
  },
  methods: {
    async loadData() {
      try {
        // 加载 Banner
        const bannerRes = await bannerApi.getList()
        this.banners = bannerRes.data || []

        // 加载业务板块
        const businessRes = await businessApi.getList()
        this.businesses = businessRes.data || []

        // 加载案例
        const caseRes = await caseApi.getList()
        this.cases = Array.isArray(caseRes.data) ? caseRes.data : []
        console.log('加载的案例数据:', this.cases)

        // 加载最新新闻
        const newsRes = await newsApi.getList({ page: 1, size: 3 })
        this.latestNews = newsRes.data || []

        // 加载统计数据
        const statsRes = await statsApi.getStats()
        this.stats = statsRes.data || [
          { icon: 'el-icon-shopping-cart-full', value: 500, label: '服务客户' },
          { icon: 'el-icon-trophy', value: 200, label: '成功项目' },
          { icon: 'el-icon-user', value: 50, label: '专业团队' },
          { icon: 'el-icon-medal', value: 100, label: '行业奖项' }
        ]

        // 初始化轮播
        this.$nextTick(() => {
          this.initSwiper()
        })
      } catch (error) {
        console.error('加载数据失败:', error)
        // 使用默认数据
        this.banners = [
          { title: '雁南时空科技', subtitle: '专业的大疆无人机代理商' }
        ]
        this.businesses = [
          { id: 1, title: '无人机销售', description: '大疆全系列无人机产品代理销售' },
          { id: 2, title: '软件开发', description: '数字高速、智能体、调度无人机平台' },
          { id: 3, title: '飞行服务', description: '承接政企无人机飞行服务' },
          { id: 4, title: '建模服务', description: '专业的无人机建模服务' }
        ]
        this.cases = []
        this.$nextTick(() => {
          this.initSwiper()
        })
      }
    },
    initSwiper() {
      if (this.$refs.swiperContainer) {
        // 确保至少有一个banner
        if (this.banners.length === 0) {
          this.banners = [{ title: '雁南时空科技', subtitle: '专业的大疆无人机代理商' }]
        }
        
        this.swiper = new Swiper(this.$refs.swiperContainer, {
          modules: [Navigation, Pagination, Autoplay, EffectFade],
          loop: this.banners.length > 1,
          autoplay: {
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: false
          },
          pagination: {
            el: '.swiper-pagination',
            clickable: true,
            type: 'bullets',
            renderBullet: function (index, className) {
              return '<span class="' + className + '"></span>'
            }
          },
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev'
          },
          effect: 'fade',
          fadeEffect: {
            crossFade: true
          },
          speed: 1000
        })
      }
    },
    filterCases(category) {
      this.activeCategory = category
    },
    viewCase(caseItem) {
      this.$router.push(`/cases/${caseItem.id}`)
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
    },
    formatSubtitle(subtitle) {
      const fallback = '专业的大疆无人机代理商，提供全方位的无人机解决方案'
      const content = subtitle && subtitle.trim() ? subtitle.trim() : fallback
      const parts = content.split(/\r?\n/).map(part => part.trim()).filter(Boolean)
      if (parts.length > 1) {
        const [tagline, ...rest] = parts
        return {
          tagline,
          text: rest.join(' ')
        }
      }
      return {
        tagline: '',
        text: content
      }
    },
    getCaseImageUrl(url) {
      if (!url || url.trim() === '') return this.defaultCaseImage
      const trimmedUrl = url.trim()
      if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
        return trimmedUrl
      }
      if (trimmedUrl.startsWith('/api/')) {
        return trimmedUrl
      }
      return `/api/file/access?path=${encodeURIComponent(trimmedUrl)}`
    },
    handleCaseImageError(event) {
      event.target.src = this.defaultCaseImage
    },
    isExternalUrl(url) {
      if (!url) return false
      const trimmedUrl = url.trim()
      return trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')
    }
  }
}
</script>

<style scoped lang="scss">
.home {
  padding-top: 80px;
}

// Banner 轮播
.banner-section {
  position: relative;
  height: 70vh;
  min-height: 500px;
  max-height: 700px;
  overflow: hidden;

  .swiper-container {
    width: 100%;
    height: 100%;

    ::v-deep .swiper-pagination {
      bottom: 40px !important;
      display: flex !important;
      justify-content: center;
      align-items: center;
      gap: 8px;
      z-index: 20;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);

      .swiper-pagination-bullet {
        width: 10px;
        height: 10px;
        background: rgba(255, 255, 255, 0.4) !important;
        opacity: 1 !important;
        border-radius: 50%;
        transition: all 0.3s;
        cursor: pointer;
        margin: 0 4px !important;
        display: inline-block;

        &:hover {
          background: rgba(255, 255, 255, 0.7) !important;
          transform: scale(1.2);
        }

        &.swiper-pagination-bullet-active {
          background: #fff !important;
          width: 12px;
          height: 12px;
          box-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
        }
      }
    }

    ::v-deep .swiper-button-next,
    ::v-deep .swiper-button-prev {
      color: #fff;
      width: 50px;
      height: 50px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      transition: all 0.3s;

      &:hover {
        background: rgba(255, 255, 255, 0.3);
      }

      &::after {
        font-size: 20px;
      }
    }
  }

  .swiper-slide {
    position: relative;
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: center;
    justify-content: center;

    .banner-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(24, 144, 255, 0.8), rgba(82, 196, 26, 0.6));
    }

    .banner-content {
      position: relative;
      z-index: 10;
      text-align: center;
      color: #fff;
      padding: 0 20px;

      .banner-title {
        font-size: 56px;
        font-weight: 700;
        margin-bottom: 20px;
        animation: fadeInUp 0.8s;
      }

      .banner-subtitle {
        max-width: 960px;
        margin: 0 auto 40px;
        padding: 0 12px;
        background: none;
        border: none;
        box-shadow: none;
        backdrop-filter: none;
        text-shadow: 0 1px 3px rgba(0, 0, 0, 0.22);
        animation: fadeInUp 1s;

        .subtitle-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 12px;
          margin-bottom: 12px;
          background: linear-gradient(120deg, rgba(255, 255, 255, 0.32), rgba(255, 255, 255, 0.16));
          border: 1px solid rgba(255, 255, 255, 0.32);
          border-radius: 999px;
          font-size: 15px;
          font-weight: 600;
          letter-spacing: 0.5px;
          box-shadow: 0 8px 18px rgba(0, 64, 128, 0.18);
        }

        .subtitle-text {
          font-size: 18px;
          line-height: 1.85;
          opacity: 0.95;
          white-space: pre-line;
        }
      }

      .btn-primary {
        display: inline-block;
        padding: 12px 40px;
        background: #fff;
        color: #1890ff;
        text-decoration: none;
        border-radius: 4px;
        font-weight: 500;
        transition: all 0.3s;
        animation: fadeInUp 1.2s;

        &:hover {
          background: #1890ff;
          color: #fff;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4);
        }
      }
    }
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 关于我们
.about-section {
  padding: 100px 0;
  background: #fff;

  .about-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 30px;

    .about-card {
      text-align: center;
      padding: 40px 20px;
      transition: all 0.3s;

      .card-icon {
        font-size: 48px;
        color: #1890ff;
        margin-bottom: 20px;
      }

      h3 {
        font-size: 20px;
        margin-bottom: 15px;
        color: #1a1a1a;
      }

      p {
        color: #666;
        line-height: 1.8;
      }

      &:hover {
        .card-icon {
          transform: scale(1.1);
        }
      }
    }
  }
}

// 业务板块
.business-section {
  padding: 100px 0;
  background: #f5f5f5;

  .business-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 30px;

    .business-card {
      overflow: hidden;

      .business-image {
        width: 100%;
        height: 200px;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
        }
      }

      .business-content {
        padding: 30px;

        h3 {
          font-size: 22px;
          margin-bottom: 15px;
          color: #1a1a1a;
        }

        p {
          color: #666;
          line-height: 1.8;
          margin-bottom: 20px;
        }
      }

      &:hover {
        .business-image img {
          transform: scale(1.1);
        }
      }
    }
  }
}

// 成功案例
.cases-section {
  padding: 100px 0;
  background: #fff;

  .case-filter {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-bottom: 50px;
    flex-wrap: wrap;

    .filter-btn {
      padding: 10px 24px;
      border: 2px solid #e8e8e8;
      background: #fff;
      color: #666;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s;
      font-size: 14px;

      &.active,
      &:hover {
        border-color: #1890ff;
        background: #1890ff;
        color: #fff;
      }
    }
  }

  .cases-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 30px;

    .case-card {
      cursor: pointer;
      overflow: hidden;

      .case-image {
        position: relative;
        width: 100%;
        height: 250px;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
        }

        .case-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(24, 144, 255, 0.9), rgba(82, 196, 26, 0.8));
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #fff;
          opacity: 0;
          transition: opacity 0.3s;

          h4 {
            font-size: 20px;
            margin-bottom: 10px;
          }

          p {
            font-size: 14px;
          }
        }
      }

      &:hover {
        .case-image {
          img {
            transform: scale(1.1);
          }

          .case-overlay {
            opacity: 1;
          }
        }
      }
    }
  }
}

// 数据统计
.stats-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  color: #fff;

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 40px;

    .stat-item {
      text-align: center;

      .stat-icon {
        font-size: 48px;
        color: #1890ff;
        margin-bottom: 20px;
      }

      .stat-number {
        font-size: 48px;
        font-weight: 700;
        margin-bottom: 10px;
        background: linear-gradient(135deg, #1890ff, #52c41a);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .stat-label {
        font-size: 16px;
        color: #ccc;
      }
    }
  }
}

// 新闻动态
.news-section {
  padding: 100px 0;
  background: #f5f5f5;

  .news-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 30px;

    .news-card {
      overflow: hidden;

      .news-image {
        position: relative;
        width: 100%;
        height: 200px;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
        }

        .news-date {
          position: absolute;
          top: 15px;
          right: 15px;
          background: rgba(24, 144, 255, 0.9);
          color: #fff;
          padding: 8px 15px;
          border-radius: 4px;
          font-size: 14px;
        }
      }

      .news-content {
        padding: 25px;

        h3 {
          font-size: 20px;
          margin-bottom: 15px;
          color: #1a1a1a;
        }

        p {
          color: #666;
          line-height: 1.8;
          margin-bottom: 15px;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .read-more {
          color: #1890ff;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.3s;

          &:hover {
            color: #096dd9;
          }
        }
      }

      &:hover {
        .news-image img {
          transform: scale(1.1);
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .banner-section {
    height: 60vh;
    min-height: 400px;

    .swiper-slide .banner-content {
      .banner-title {
        font-size: 32px;
      }

      .banner-subtitle {
        padding: 12px 16px;
        margin-bottom: 28px;

        .subtitle-tag {
          margin-bottom: 10px;
          font-size: 14px;
          padding: 5px 10px;
        }

        .subtitle-text {
          font-size: 15px;
          line-height: 1.65;
        }
      }
    }
  }

  .about-section,
  .business-section,
  .cases-section,
  .news-section {
    padding: 60px 0;
  }
}
</style>

